/**
 * Orchestra AI Platform - Keyboard Shortcuts Hook
 * 
 * Hook for handling keyboard shortcuts with modifier keys
 * 
 * @fileoverview Keyboard shortcuts handler with comprehensive modifier support
 */

import { useEffect } from 'react';

/**
 * @typedef {Object} Shortcut
 * @property {string} key - Key to press
 * @property {boolean} [ctrl] - Require Ctrl key
 * @property {boolean} [shift] - Require Shift key
 * @property {boolean} [alt] - Require Alt key
 * @property {Function} action - Action to execute
 * @property {string} description - Description of shortcut
 */

/**
 * Hook for keyboard shortcuts
 * @param {Shortcut[]} shortcuts - Array of shortcut definitions
 * @param {boolean} [enabled=true] - Whether shortcuts are enabled
 */
export function useKeyboardShortcuts(shortcuts, enabled = true) {
  useEffect(() => {
    if (!enabled || !shortcuts || shortcuts.length === 0) return;

    /**
     * Handle keydown event
     * @param {KeyboardEvent} event
     */
    const handleKeyDown = (event) => {
      for (const shortcut of shortcuts) {
        const ctrlMatch = shortcut.ctrl
          ? event.ctrlKey || event.metaKey
          : !event.ctrlKey && !event.metaKey;
        const shiftMatch = shortcut.shift ? event.shiftKey : !event.shiftKey;
        const altMatch = shortcut.alt ? event.altKey : !event.altKey;

        if (
          event.key.toLowerCase() === shortcut.key.toLowerCase() &&
          ctrlMatch &&
          shiftMatch &&
          altMatch
        ) {
          event.preventDefault();
          shortcut.action();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [shortcuts, enabled]);
}

